import { OrgNameAddress } from '@bb/domain/dist/types';
import type { User } from '@bb/users';
import {
  Button as OldButton,
  SemanticColor,
  Table as OldTable,
  Tabs as OldTabs,
} from '@brokerbay/elements-web';
import {
  Button as NewButton,
  Table as NewTable,
  Tabs as NewTabs,
} from '@bb/elements-web-rebranding';
import { Icon, Pagination } from 'components';
import { useBreakpoint } from 'hooks';
import { omit } from 'lodash';
import type { HtmlHTMLAttributes } from 'react';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { featureEnabled } from 'utils';

type RecipientsTableProps = {
  uniqueUserData: User[];
  ommittedUserData: User[];
  locationIndex: Record<string, OrgNameAddress>;
  pageSize: number;
  totalUniqueUsers: number;
  currentPage: number;
  activeTab: 'recipients' | 'omitted';
  onActiveTabChange: (tab: 'recipients' | 'omitted') => void;
  onPageChange: (page: number) => void;
  onOmitUser: (user: User) => void;
  onReinstateUser: (user: User) => void;
  isLoading?: boolean;
};

const RecipientsTable: React.FC<RecipientsTableProps> = ({
  uniqueUserData,
  ommittedUserData,
  locationIndex,
  pageSize,
  totalUniqueUsers,
  currentPage,
  activeTab,
  onPageChange,
  onActiveTabChange,
  onOmitUser,
  onReinstateUser,
  isLoading,
}) => {
  const hasSupraRebrandingFeature = useMemo(
    () => featureEnabled('supraRebrandingFeature', 1),
    [],
  );

  const Tabs = useMemo(
    () => (hasSupraRebrandingFeature ? NewTabs : OldTabs),
    [hasSupraRebrandingFeature],
  );

  const StyledTable = useMemo(
    () => (hasSupraRebrandingFeature ? NewStyledTable : OldStyledTable),
    [hasSupraRebrandingFeature],
  );

  const StyledIconButton = useMemo(
    () =>
      hasSupraRebrandingFeature ? NewStyledIconButton : OldStyledIconButton,
    [hasSupraRebrandingFeature],
  );

  const screens = useBreakpoint();
  const isSimplePagination = screens.lg && !screens.xl;

  const columns = useMemo(
    () => [
      {
        title: 'First Name',
        dataIndex: 'firstName',
        key: 'firstName',
        ellipsis: true,
        onCell: () =>
          ({ 'data-cy': 'first-name-td' }) as HtmlHTMLAttributes<any>,
      },
      {
        title: 'Last Name',
        dataIndex: 'lastName',
        key: 'lastName',
        ellipsis: true,
        onCell: () =>
          ({ 'data-cy': 'last-name-td' }) as HtmlHTMLAttributes<any>,
      },
      {
        title: 'Branch',
        dataIndex: '_location',
        key: '_location',
        onCell: () => ({ 'data-cy': 'branch-td' }) as HtmlHTMLAttributes<any>,
      },
      {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
        ellipsis: true,
        onCell: () => ({ 'data-cy': 'email-td' }) as HtmlHTMLAttributes<any>,
      },
      {
        title: '',
        key: 'action',
        width: 50,
        onCell: () => ({ 'data-cy': 'actions-td' }) as HtmlHTMLAttributes<any>,
        render: (_: any, record: User) =>
          activeTab === 'recipients' ? (
            <StyledIconButton
              type="primary"
              size="small"
              dataCy="omit-user-button"
              minWidth={24}
              danger
              icon={
                <Icon
                  type="antdCloseOutlined"
                  color="white"
                  width={14}
                  height={14}
                />
              }
              onClick={() => onOmitUser(omit(record, '_location') as User)}
              loading={isLoading}
            />
          ) : (
            <StyledIconButton
              type="primary"
              size="small"
              minWidth={24}
              dataCy="reinstate-user-button"
              icon={<Icon type="undo" color="white" width={14} height={14} />}
              onClick={() => onReinstateUser(omit(record, '_location') as User)}
              loading={isLoading}
            />
          ),
      },
    ],
    [activeTab, onOmitUser, onReinstateUser, isLoading, StyledIconButton],
  );

  const tableData = useMemo(() => {
    let data: User[];

    if (activeTab === 'recipients') {
      data = uniqueUserData;
    } else {
      const startIndex = (currentPage - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      data = ommittedUserData.slice(startIndex, endIndex);
    }
    data = data || [];

    return data.map((user: User) => {
      const location = locationIndex[user.address]?.name || '';
      return {
        ...user,
        _location: location,
      };
    });
  }, [
    activeTab,
    uniqueUserData,
    ommittedUserData,
    locationIndex,
    currentPage,
    pageSize,
  ]);

  const omittedUsersCount = ommittedUserData?.length || 0;

  const totalItems =
    (activeTab === 'recipients' ? totalUniqueUsers : omittedUsersCount) || 0;

  const handleTabChange = (key: string) => {
    onActiveTabChange(key as 'recipients' | 'omitted');
    onPageChange(1);
  };

  return (
    <div>
      <TabHeaderContainer>
        <Tabs
          defaultActiveKey=""
          type="card"
          activeKey={activeTab}
          onChange={handleTabChange}
          items={[
            {
              key: 'recipients',
              label: <span data-cy="recipients-table-tab">Recipients</span>,
            },
            {
              key: 'omitted',
              label: (
                <span data-cy="omitted-table-tab">
                  Omitted Users ({omittedUsersCount})
                </span>
              ),
            },
          ]}
          dataCy="recipients-table-tabs"
        />
        <StyledPagination
          current={currentPage}
          defaultPageSize={10}
          pageSize={pageSize}
          total={totalItems}
          onChange={onPageChange}
          showSizeChanger={false}
          showLessItems
          simple={isSimplePagination}
        />
      </TabHeaderContainer>
      <StyledTable
        rowKey="id"
        size="small"
        dataCy="recipients-table"
        columns={columns}
        dataSource={tableData}
        pagination={false}
        onRow={(data: User) =>
          ({
            'data-cy': `user-tr-${data.id}`,
          }) as HtmlHTMLAttributes<any>
        }
      />
    </div>
  );
};

const TabHeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0;

  .ant-tabs,
  .ant5-tabs {
    flex: 1;
    margin-bottom: 0;
  }

  .ant-tabs-nav,
  .ant5-tabs-nav {
    margin-bottom: 0 !important;
  }
`;

const StyledPagination = styled(Pagination)`
  .ant-pagination-item,
  .ant-pagination-prev,
  .ant-pagination-next {
    border-radius: 0;
    margin: 0 2px;
  }
`;

const OldStyledTable = styled(OldTable)`
  border: 1px solid #d9d9d9;
  border-radius: 0;

  &&& thead tr th {
    border-radius: 0;
  }
`;

const NewStyledTable = styled(NewTable)`
  border: 1px solid #d9d9d9;
  border-radius: 0;

  &&& thead tr th {
    border-radius: 0;
  }
`;

const OldStyledIconButton = styled(OldButton)`
  && {
    padding: 4px 10px;
    line-height: 1;
  }
  && .ant5-btn-loading-icon > .anticon-loading {
    color: ${SemanticColor.light};
  }
`;

const NewStyledIconButton = styled(NewButton)`
  && {
    padding: 4px 10px;
    line-height: 1;
  }
  && .ant5-btn-loading-icon > .anticon-loading {
    color: ${SemanticColor.light};
  }
`;

export default RecipientsTable;
